import { supabase } from '@/integrations/supabase/client';
import {
  getPendingReports,
  getPendingCheckins,
  getPendingIncidents,
  removePendingReport,
  removePendingCheckin,
  removePendingIncident,
  updateReportRetryCount,
  updateCheckinRetryCount,
  updateIncidentRetryCount,
} from './offlineStorage';

const MAX_RETRIES = 3;

interface SyncResult {
  success: boolean;
  synced: number;
  retryingFailed: number;
  removed: number;
  errors: string[];
}

interface ItemSyncResult {
  synced: number;
  retryingFailed: number;
  removed: number;
  errors: string[];
}

// Sync pending hazard reports
async function syncPendingReports(): Promise<ItemSyncResult> {
  const reports = await getPendingReports();
  let synced = 0;
  let retryingFailed = 0;
  let removed = 0;
  const errors: string[] = [];

  for (const report of reports) {
    try {
      // Safety net: Remove old stuck entries that already hit MAX_RETRIES
      if (report.retryCount >= MAX_RETRIES) {
        console.log(`[SyncService] Removing stuck report ${report.id} (retryCount already ${report.retryCount})`);
        await removePendingReport(report.id);
        errors.push(`Report ${report.id}: Removed (was stuck at max retries)`);
        removed++;
        continue;
      }

      // Upload photo if exists
      let photoUrl = null;
      if (report.photoBlob && report.data.reporter_id) {
        const fileName = `${report.data.reporter_id}/${Date.now()}_offline_photo.jpg`;
        const { error: uploadError, data: uploadData } = await supabase.storage
          .from('hazard-photos')
          .upload(fileName, report.photoBlob);

        if (uploadError) {
          console.error('Photo upload failed:', uploadError);
          // Continue without photo
        } else {
          const { data: { publicUrl } } = supabase.storage
            .from('hazard-photos')
            .getPublicUrl(fileName);
          photoUrl = publicUrl;
        }
      }

      // CLEAN report data - remove fields that don't exist in peer_reports table
      const {
        company_id,
        synced_from_offline,
        ...cleanData
      } = report.data as any;

      const reportData = {
        ...cleanData,
        photo_url: photoUrl || cleanData.photo_url,
      };

      const { error: insertError } = await supabase
        .from('peer_reports')
        .insert(reportData);

      if (insertError) {
        throw insertError;
      }

      // Success - remove from offline storage
      await removePendingReport(report.id);
      synced++;
    } catch (error: any) {
      console.error(`Failed to sync report ${report.id}:`, error);
      
      const nextRetry = (report.retryCount || 0) + 1;
      if (nextRetry >= MAX_RETRIES) {
        // Delete immediately on 3rd failure
        console.log(`[SyncService] Removing report ${report.id} after ${nextRetry} failed attempts`);
        await removePendingReport(report.id);
        errors.push(`Report ${report.id}: Dihapus setelah ${nextRetry} kali gagal`);
        removed++;
      } else {
        await updateReportRetryCount(report.id, nextRetry);
        errors.push(`Report ${report.id}: Gagal (percobaan ${nextRetry}/${MAX_RETRIES})`);
        retryingFailed++;
      }
    }
  }

  return { synced, retryingFailed, removed, errors };
}

// Sync pending check-ins
async function syncPendingCheckins(): Promise<ItemSyncResult> {
  const checkins = await getPendingCheckins();
  let synced = 0;
  let retryingFailed = 0;
  let removed = 0;
  const errors: string[] = [];

  for (const checkin of checkins) {
    try {
      // Safety net: Remove old stuck entries that already hit MAX_RETRIES
      if (checkin.retryCount >= MAX_RETRIES) {
        console.log(`[SyncService] Removing stuck checkin ${checkin.id} (retryCount already ${checkin.retryCount})`);
        await removePendingCheckin(checkin.id);
        errors.push(`Checkin ${checkin.id}: Removed (was stuck at max retries)`);
        removed++;
        continue;
      }

      const checkinData = {
        ...checkin.data,
      };

      const { error: insertError } = await supabase
        .from('shift_checkins')
        .insert(checkinData);

      if (insertError) {
        throw insertError;
      }

      await removePendingCheckin(checkin.id);
      synced++;
    } catch (error: any) {
      console.error(`Failed to sync checkin ${checkin.id}:`, error);
      
      const nextRetry = (checkin.retryCount || 0) + 1;
      if (nextRetry >= MAX_RETRIES) {
        // Delete immediately on 3rd failure
        console.log(`[SyncService] Removing checkin ${checkin.id} after ${nextRetry} failed attempts`);
        await removePendingCheckin(checkin.id);
        errors.push(`Checkin ${checkin.id}: Dihapus setelah ${nextRetry} kali gagal`);
        removed++;
      } else {
        await updateCheckinRetryCount(checkin.id, nextRetry);
        errors.push(`Checkin ${checkin.id}: Gagal (percobaan ${nextRetry}/${MAX_RETRIES})`);
        retryingFailed++;
      }
    }
  }

  return { synced, retryingFailed, removed, errors };
}

// Sync pending incidents
async function syncPendingIncidents(): Promise<ItemSyncResult> {
  const incidents = await getPendingIncidents();
  let synced = 0;
  let retryingFailed = 0;
  let removed = 0;
  const errors: string[] = [];

  for (const incident of incidents) {
    try {
      // Safety net: Remove old stuck entries that already hit MAX_RETRIES
      if (incident.retryCount >= MAX_RETRIES) {
        console.log(`[SyncService] Removing stuck incident ${incident.id} (retryCount already ${incident.retryCount})`);
        await removePendingIncident(incident.id);
        errors.push(`Incident ${incident.id}: Removed (was stuck at max retries)`);
        removed++;
        continue;
      }

      // Call edge function to create incident - exclude synced_from_offline from body
      const { error: createError } = await supabase.functions.invoke('create-incident', {
        body: {
          location: incident.data.location,
          description: incident.data.description,
          incident_datetime: incident.data.incident_datetime,
          injury_severity: incident.data.injury_severity,
          area_safety_status: incident.data.area_safety_status,
          reported_by: incident.data.reported_by,
          company_id: incident.data.company_id,
          site_id: incident.data.site_id,
        },
      });

      if (createError) {
        throw createError;
      }

      await removePendingIncident(incident.id);
      synced++;
    } catch (error: any) {
      console.error(`Failed to sync incident ${incident.id}:`, error);
      
      const nextRetry = (incident.retryCount || 0) + 1;
      if (nextRetry >= MAX_RETRIES) {
        // Delete immediately on 3rd failure
        console.log(`[SyncService] Removing incident ${incident.id} after ${nextRetry} failed attempts`);
        await removePendingIncident(incident.id);
        errors.push(`Incident ${incident.id}: Dihapus setelah ${nextRetry} kali gagal`);
        removed++;
      } else {
        await updateIncidentRetryCount(incident.id, nextRetry);
        errors.push(`Incident ${incident.id}: Gagal (percobaan ${nextRetry}/${MAX_RETRIES})`);
        retryingFailed++;
      }
    }
  }

  return { synced, retryingFailed, removed, errors };
}

// Main sync function
export async function syncAllPending(): Promise<SyncResult> {
  console.log('[SyncService] Starting sync...');
  
  try {
    const [reportResults, checkinResults, incidentResults] = await Promise.all([
      syncPendingReports(),
      syncPendingCheckins(),
      syncPendingIncidents(),
    ]);

    const result: SyncResult = {
      success: 
        reportResults.retryingFailed === 0 && 
        checkinResults.retryingFailed === 0 && 
        incidentResults.retryingFailed === 0 &&
        reportResults.removed === 0 && 
        checkinResults.removed === 0 && 
        incidentResults.removed === 0,
      synced: reportResults.synced + checkinResults.synced + incidentResults.synced,
      retryingFailed: reportResults.retryingFailed + checkinResults.retryingFailed + incidentResults.retryingFailed,
      removed: reportResults.removed + checkinResults.removed + incidentResults.removed,
      errors: [...reportResults.errors, ...checkinResults.errors, ...incidentResults.errors],
    };

    console.log('[SyncService] Sync complete:', result);
    return result;
  } catch (error: any) {
    console.error('[SyncService] Sync failed:', error);
    return {
      success: false,
      synced: 0,
      retryingFailed: 1,
      removed: 0,
      errors: [error.message],
    };
  }
}

// Auto-sync when coming back online
export function setupAutoSync(onSyncComplete?: (result: SyncResult) => void) {
  const handleOnline = async () => {
    console.log('[SyncService] Device back online, starting auto-sync...');
    const result = await syncAllPending();
    onSyncComplete?.(result);
  };

  window.addEventListener('online', handleOnline);

  return () => {
    window.removeEventListener('online', handleOnline);
  };
}
